import { createApi } from '@reduxjs/toolkit/query/react';
import { type OnboardingStatus } from '@strata/react-status-indicators';

import { ONBOARDING_API_URL } from 'api/APIBase';
import { handleError } from 'services/Error';
import { getBaseQuery } from 'store/api/apiBaseQuery';

export interface ShippingInfo {
    tracking_number: string;
    shipment_date?: string;
    shipment_carrier?: string;
}

export interface ShippingInfoBody {
    tracking_number: string;
    shipment_date?: string | null;
    shipment_carrier?: string | null;
}

const TagTypes = {
    SHIPPING_INFO: 'SHIPPING_INFO',
} as const;

export const onboardingApi = createApi({
    baseQuery: getBaseQuery(ONBOARDING_API_URL),
    tagTypes: Object.values(TagTypes),
    reducerPath: 'onboardingApi',
    endpoints: (build) => ({
        getShippingInfo: build.query<ShippingInfo, { referralId: string }>({
            query: ({ referralId }) => `supplier/shipping/${referralId}`,
            providesTags: (_results, _error, { referralId }) => [
                { type: TagTypes.SHIPPING_INFO, id: referralId },
            ],
            transformErrorResponse: (err) => {
                if (err.status !== 404) {
                    handleError('An error occurred retrieving shipping information.', err);
                }
            },
        }),
        postShippingInfo: build.mutation<
            void,
            { referralId: string; payerId: string; body: ShippingInfoBody }
        >({
            query: ({ referralId, payerId, body }) => ({
                url: `supplier/shipping/${referralId}`,
                params: {
                    payer_id: payerId,
                },
                method: 'POST',
                body,
            }),
            invalidatesTags: (_results, _error, { referralId }) => [
                { type: TagTypes.SHIPPING_INFO, id: referralId },
            ],
        }),
        getOnboardingStatus: build.query<OnboardingStatus, string>({
            query: (referralId) => ({
                url: `supplier/onboarding_status/${referralId}`,
                method: 'GET',
            }),
            transformErrorResponse: (err) => {
                if (err.status !== 404) {
                    handleError('An error occurred retrieving onboarding status.', err);
                }
            },
        }),
    }),
});

export const { useGetShippingInfoQuery, usePostShippingInfoMutation, useGetOnboardingStatusQuery } =
    onboardingApi;
